import type {
  ILinkChain,
  IMark,
  INewUserEntry,
  INote,
  ISchema,
  ISchemaWithText,
  IUserEntry,
} from "~/models/IUserEntry";
import type { LocalSyncPayload } from "~/composables/entries/useEntries";
import type { ColorPicker } from "~/types/ColorPicker";

type EntryTypeLimit = Record<IUserEntry["type"], number>;

export function useLocalEntriesSync() {
  const { warning } = useToast();
  const localEntries = useLocalEntries();
  const localBookmarks = useLocalBookmarks();
  const { isActive } = useActiveEntryRouteParams();
  const { setActive } = useSetActiveEntry();
  const { loggedIn } = storeToRefs(useUserStore());
  const { t } = useI18n();
  const { openModal } = useGuestAlert();

  const { createBookmarkMutation } = bookmarksMutations();
  const { createEntryMutation } = entriesMutations();

  const isShowcase = useExamShowcase();

  const entriesLimit: EntryTypeLimit = {
    underline: 20,
    mark: 20,
    link: 10,
    schema: 5,
    note: 10,
    link_chain: 10,
  };

  const isLimitReached = computed(() => {
    if (isShowcase) return false;

    if (loggedIn.value) {
      return false;
    }

    for (const entry in entriesLimit) {
      const entryLimit = localEntries.value.filter(
        (localEntry) => localEntry.payload.entry.type === entry
      ).length;

      if (entryLimit >= entriesLimit[entry]) return true;
    }

    return false;
  });

  async function syncEntries(
    entries: typeof localEntries.value,
    bookmarks: typeof localBookmarks.value
  ) {
    let activeLocalEntry: ISchema | INote | null = null;

    const promises = entries.map(async (entry) => {
      let addendum = {};
      if (entry.payload.entry.type === "schema") {
        addendum = { text: (entry.data as ISchemaWithText).text };
      } else if (entry.payload.entry.type === "note") {
        addendum = { color: (entry.data as INote).color };
      } else if (entry.payload.entry.type === "mark") {
        const markEntry = entry.data as IMark;
        addendum = {
          color: markEntry.color,
          color_type: markEntry.color_type,
        } satisfies ColorPicker;
      }
      const entryIsActive = isActive(entry.data);
      const updatedEntry = await createEntryMutation.mutateAsync({
        paragraphId: entry.payload.paragraphId,
        entry: {
          ...entry.payload.entry,
          ...addendum,
        },
      });

      localEntries.value = localEntries.value.filter(
        ({ data: { id, type } }) =>
          !(id === entry.data.id && type === entry.data.type)
      );

      if (entryIsActive) {
        activeLocalEntry = updatedEntry as ISchemaWithText | INote;
      }
    });

    const bookmarkPromises = bookmarks.map(async (localBookmark) => {
      const { mutateAsync } = createBookmarkMutation;
      const color: ColorPicker = {
        color: localBookmark.color,
        color_type: localBookmark.color_type,
      };
      await mutateAsync({
        color,
        paragraphId: localBookmark.legal_text_paragraph_id,
      });

      localBookmarks.value = localBookmarks.value.filter(
        ({ id }) => id === localBookmark.id
      );
    });

    await Promise.all([...promises, ...bookmarkPromises]);

    localEntries.value = [];
    localBookmarks.value = [];

    if (activeLocalEntry) {
      await setActive(activeLocalEntry);
    }
  }

  function canAddEntry(entryType: keyof EntryTypeLimit) {
    if (isShowcase) return true;

    const limit = entriesLimit[entryType];
    const currentCount = localEntries.value.filter(
      (localEntry) => localEntry.payload.entry.type === entryType
    ).length;

    return currentCount < limit;
  }

  function deleteEntry(entry: IUserEntry) {
    localEntries.value = localEntries.value.filter(
      (localEntry) => localEntry.data.id !== entry.id
    );

    // do the same but for links in link chains
    if (entry.type === "link") {
      const localChain = localEntries.value.find(
        ({ data }) =>
          data.type === "link_chain" && data.id === entry.link_chain_id
      ) as LocalSyncPayload<any, ILinkChain> | null;
      if (localChain) {
        localChain.data.links = localChain.data.links.filter(
          (link) => link.id !== entry.id
        );
      }
    }
  }

  function updateEntry(entry: LocalSyncPayload<INewUserEntry>) {
    const entryIndex = localEntries.value.findIndex(
      (localEntry) => localEntry.data.id === entry.data.id
    );
    if (entryIndex !== -1) {
      localEntries.value = [
        ...localEntries.value.slice(0, entryIndex),
        entry,
        ...localEntries.value.slice(entryIndex + 1),
      ];
    } else {
      localEntries.value = [...localEntries.value, entry];
    }
  }

  function showAlert() {
    if (isShowcase) return;

    if (
      // Show Alert when First Entry or 5th time entry
      localEntries.value.length === 1 ||
      localEntries.value.length % 5 === 0
    ) {
      openModal(
        t("authentication.welcome"),
        t("authentication.not_logged_in_personalize")
      );
    }
  }

  const showLimitReached = () => {
    openModal(
      t("authentication.register_free_now"),
      t("authentication.limit_reached_editing")
    );

    warning(t("authentication.limit_reached"));
  };

  function addEntry(entry: LocalSyncPayload<INewUserEntry>) {
    const entryIndex = localEntries.value.findIndex(
      (localEntry) => localEntry.data.id === entry.data.id
    );

    // fix for links
    if (entry.data.type === "link_chain") {
      const chain = entry.data as ILinkChain;
      chain.links = chain.links.map((link) => ({
        ...link,
        id: Math.floor(Math.random() * 1000000),
        link_chain_id: chain.id,
      }));
    }

    if (entryIndex !== -1) {
      localEntries.value = [
        ...localEntries.value.slice(0, entryIndex),
        entry,
        ...localEntries.value.slice(entryIndex + 1),
      ];
    } else {
      if (canAddEntry(entry.payload.entry.type)) {
        localEntries.value = [...localEntries.value, entry];
        showAlert();

        if (isLimitReached.value) {
          showLimitReached();
        }
      } else {
        showLimitReached();
        throw new Error("LIMIT_REACHED");
      }
    }
  }

  return {
    addEntry,
    updateEntry,
    syncEntries,
    deleteEntry,
    isLimitReached,
  };
}
